import React, { useState, useEffect, useRef } from 'react';
import { PieChart, Pie, Tooltip } from 'recharts';
import "../../styles/network-performance/network-performance-charts.scss";

type SpeedometerGaugeProps = {
  maxValue?: number;
  label?: string;
  scores: { benchmarkScore: number, networkScore: number, scenarioScore: number }
};

const SpeedometerGauge: React.FC<SpeedometerGaugeProps> = ({
  maxValue = 100,
  label,
  scores,
}) => {

  type Needle = {
    animatedScore: number;
    cx: number;
    cy: number;
    iR: number;
    oR: number;
    color: string;
  };

  const averageScore = getAverage(scores, maxValue);

  const RADIAN = Math.PI / 180;

  const [animatedScore, setAnimatedScore] = useState(0);
  const prevScoreRef = useRef(0);

  useEffect(() => {
    let frame: number;
    const duration = 600; // ms
    const start = performance.now();
    const startScore = prevScoreRef.current;
    const endScore = averageScore;

    function easeOutCubic(t: number): number {
      return 1 - Math.pow(1 - t, 3);
    }

    function animate(now: number) {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);
      const easedProgress = easeOutCubic(progress);
      const current = startScore + (endScore - startScore) * easedProgress;
      setAnimatedScore(current);

      if (progress < 1) {
        frame = requestAnimationFrame(animate);
      } else {
        prevScoreRef.current = endScore;
      }
    }

    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [averageScore]);

  const cx = 115;
  const cy = 115;
  const iR = 50;
  const oR = 100;

  const needle = ({ animatedScore, cx, cy, iR, oR, color }: Needle) => {
    const ang = getNeedleAngle(animatedScore, maxValue);
    const length = (iR + 2 * oR) / 3;
    const r = 5;

    // Center the needle at the actual chart center
    const x0 = cx;
    const y0 = cy;
    const sin = Math.sin(-RADIAN * ang);
    const cos = Math.cos(-RADIAN * ang);
    const xba = x0 + r * sin;
    const yba = y0 - r * cos;
    const xbb = x0 - r * sin;
    const ybb = y0 + r * cos;
    const xp = x0 + length * cos;
    const yp = y0 + length * sin;

    return [
      <circle key="needle-circle" cx={x0} cy={y0} r={r} fill={color} stroke="none" />,
      <path
        key="needle-path"
        d={`M${xba} ${yba}L${xbb} ${ybb} L${xp} ${yp} L${xba} ${yba}`}
        stroke="none"
        fill={color}
      />,
    ];
  };

  const getNeedleAngle = (average: number, max: number) => {
    const clamped = Math.max(0, Math.min(average, max));
    return 180 - (clamped / max) * 180;
  };

  function getAverage(
    scores: { [key: string]: number },
    maxValue: number
  ): number {
    const values = Object.values(scores);
    const avg = values.reduce((a, b) => a + b, 0) / values.length;

    if (maxValue < 100) {
      return parseFloat(avg.toFixed(1));
    } else {
      return Math.round(avg);
    }
  }

  type CustomPieTooltipProps = {
    active?: boolean;
  };

  const CustomPieTooltip: React.FC<CustomPieTooltipProps> = ({ active }) => {
    if (!active) return null;

    const pieData = [
      { fill: "#8884d8", name: "Your Network", value: scores.networkScore },
      { fill: "#82ca9d", name: "Benchmark", value: scores.benchmarkScore },
      { fill: "#fce77d", name: "Scenario", value: scores.scenarioScore },
    ];

    return (
      <div className="speedometerGauge__tooltipContainer">
        {pieData.map((entry) => (
          <div key={entry.name} className="speedometerGauge__flexCenterRow">
            <div className="speedometerGauge__iconBox" style={{ "--icon-fill": entry.fill } as React.CSSProperties} />
            <span className="speedometerGauge__segmentLabelValue">{`${entry.name}: ${entry.value}`}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div style={{ position: "relative", textAlign: "center", }}>
      <PieChart width={230} height={230} className="speedometerGauge__pieChart">
        <Pie
          data={[{ name: "Your Network", value: scores.networkScore }]}
          dataKey="value"
          cx="50%"
          cy="50%"
          startAngle={180}
          endAngle={getNeedleAngle(scores.networkScore, maxValue)}
          innerRadius={65}
          outerRadius={75}
          label={false}
          fill="#fce77d"
        />
        <Pie
          data={[{ name: "Benchmark", value: scores.benchmarkScore }]}
          dataKey="value"
          cx="50%"
          cy="50%"
          startAngle={180}
          endAngle={getNeedleAngle(scores.benchmarkScore, maxValue)}
          innerRadius={75}
          outerRadius={85}
          label={false}
          fill="#82ca9d"
        />
        <Pie
          data={[{ name: "Scenario", value: scores.scenarioScore }]}
          dataKey="value"
          cx="50%"
          cy="50%"
          startAngle={180}
          endAngle={getNeedleAngle(scores.scenarioScore, maxValue)}
          innerRadius={85}
          outerRadius={95}
          label={false}
          fill="#8884d8"
        />

        {/*Top arc dashed line*/}
        <path
          d="M 15 115 A 100 100 0 0 1 215 115" 
          stroke="#555"
          strokeWidth={6}
          strokeDasharray="2 3"
          fill="none"
        />
        {needle({ animatedScore, color: '#000000', cx, cy, iR, oR })}
        <Tooltip content={<CustomPieTooltip active={true} />} />
      </PieChart>

      <div className="speedometerGauge__averageScore">
        <span style={{ fontSize: "2rem" }}>
          <b>{averageScore}</b>
        </span>/{maxValue}
      </div>

      <div className="speedometerGauge__pieChartLabel">{label}</div>
    </div>
  );
};

export default SpeedometerGauge;